/**
 * Individual (Donor) entity index page
 */

import { EntityIndex } from './base';
import { canonicalPath } from '@electinfo/common';
import { matchLeafDetailPath } from './leaf-detail';
import { loadIndividualDetail } from './leaf-profiles';
import type { EntityConfig, EntitySearchResult, FilterControl } from './types';
import { US_STATE_OPTIONS } from './us-states';

function formatCurrency(amount: number): string {
  if (amount >= 1_000_000_000) {
    return `$${(amount / 1_000_000_000).toFixed(1)}B`;
  } else if (amount >= 1_000_000) {
    return `$${(amount / 1_000_000).toFixed(1)}M`;
  } else if (amount >= 1_000) {
    return `$${(amount / 1_000).toFixed(0)}K`;
  }
  return `$${amount.toLocaleString()}`;
}

const jan1 = (year: number): number => Date.UTC(year, 0, 1);

/** Election cycles offered by the cycle filter, newest first. */
const CYCLES = Array.from({ length: 14 }, (_, i) => String(2026 - 2 * i));

/**
 * Filters (api.elect.info INDIVIDUAL_FILTER_SPECS, sites#277). County comes
 * from the donor's ZIP (PostalCode-OVERLAPS->County, largest land overlap);
 * the dropdown stays hidden until the individuals index carries it. Street
 * matches the donor's latest e-filed street line (filter only, not shown).
 */
const INDIVIDUAL_FILTERS: FilterControl[] = [
  { key: 'state', label: 'State', type: 'select', options: US_STATE_OPTIONS.map(([code, name]) => ({ value: code, label: name })) },
  { key: 'county', label: 'County', type: 'facet', dependsOn: ['state'], size: 300 },
  { key: 'cycle', label: 'Gave in cycle', type: 'checks', options: CYCLES.map(c => ({ value: c, label: c })) },
  {
    key: 'total', label: 'Lifetime total', type: 'bands', bands: [
      { id: '200', label: '$200+', min: 200 }, { id: '1000', label: '$1K+', min: 1000 },
      { id: '10000', label: '$10K+', min: 10000 }, { id: '100000', label: '$100K+', min: 100000 },
      { id: '1000000', label: '$1M+', min: 1000000 }
    ]
  },
  {
    key: 'lean', label: 'Party lean (giving)', type: 'bands', bands: [
      { id: 'strong-d', label: 'Strong Democratic (80%+ D)', min: 0.8 },
      { id: 'lean-d', label: 'Lean Democratic (60–80% D)', min: 0.6, max: 0.7999 },
      { id: 'mixed', label: 'Mixed (40–60% D)', min: 0.4, max: 0.5999 },
      { id: 'lean-r', label: 'Lean Republican (60–80% R)', min: 0.2, max: 0.3999 },
      { id: 'strong-r', label: 'Strong Republican (80%+ R)', max: 0.1999 }
    ]
  },
  {
    key: 'last_gave', label: 'Last gave', type: 'bands', bands: [
      { id: '2025', label: 'In 2025–26', min: jan1(2025) }, { id: '2023', label: 'Since 2023', min: jan1(2023) },
      { id: '2021', label: 'Since 2021', min: jan1(2021) }, { id: 'pre2021', label: 'Not since 2020', max: jan1(2021) - 1 }
    ]
  },
  { key: 'employer', label: 'Employer', type: 'text', placeholder: 'e.g. Google' },
  { key: 'occupation', label: 'Occupation', type: 'text', placeholder: 'e.g. attorney' },
  { key: 'city', label: 'City', type: 'text', placeholder: 'e.g. Austin' },
  { key: 'street', label: 'Street', type: 'text', placeholder: 'e.g. 1200 Congress Ave' },
  { key: 'zip', label: 'ZIP', type: 'text', placeholder: '787 or 78701' }
];

class IndividualIndex extends EntityIndex {
  protected setup(): void {
    const detailId = matchLeafDetailPath('individuals');
    if (detailId) {
      void loadIndividualDetail(detailId);
      return;
    }
    super.setup();
  }

  getConfig(): EntityConfig {
    return {
      entityType: 'Individual',
      placeholder: 'Search individual donors by name, city, employer...',
      browseTitle: 'All Individual Donors',
      sortField: 'total_contributions',
      sortOrder: 'DESC',
      filterControls: INDIVIDUAL_FILTERS,
      formatResult: (result: EntitySearchResult) => {
        const city = result.city || '';
        const state = result.state || '';
        const location = [city, state].filter(Boolean).join(', ');

        const metadata = result.metadata ? JSON.parse(result.metadata) : {};
        const employer = metadata.employer || '';
        const occupation = metadata.occupation || '';
        const work = [occupation, employer].filter(Boolean).join(' at ');

        const totalContributions = result.score ? formatCurrency(result.score) : '';
        const metaParts = [location, work].filter(Boolean);
        if (totalContributions) {
          metaParts.push(`${totalContributions} contributed`);
        }
        const meta = metaParts.join(' | ');

        return {
          url: `/${canonicalPath('Individual', result.id)}/`,
          name: result.name || '',
          meta: meta || 'Individual Donor'
        };
      }
    };
  }
}

new IndividualIndex();
